import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { useAuth } from '@/hooks/useAuth'
import { renovacaoService } from '@/services/renovacaoService'
import type { SolicitacaoRenovacao, StatusSolicitacaoRenovacao } from '@/types/renovacao'
import { formatDate } from '@/utils/formatters'

const STATUS_COR: Record<
  StatusSolicitacaoRenovacao,
  'default' | 'info' | 'warning' | 'success' | 'error'
> = {
  SOLICITADA: 'warning',
  EM_ANALISE: 'info',
  APROVADA: 'success',
  REPROVADA: 'error',
  CANCELADA: 'default',
}

const STATUS_LABEL: Record<StatusSolicitacaoRenovacao, string> = {
  SOLICITADA: 'Solicitada',
  EM_ANALISE: 'Em análise',
  APROVADA: 'Aprovada',
  REPROVADA: 'Reprovada',
  CANCELADA: 'Cancelada',
}

/** Em aberto = ainda espera decisão da Originei. */
const EM_ABERTO: StatusSolicitacaoRenovacao[] = ['SOLICITADA', 'EM_ANALISE']

/**
 * Papéis da Originei — só eles decidem. Espelha o @PreAuthorize do endpoint de
 * análise; o backend continua sendo a autoridade.
 */
const ROLES_ANALISE = ['ADMIN', 'GERENTE', 'OPERADOR']

type Decisao = 'APROVADA' | 'REPROVADA'

const RenovacoesPage: FC = () => {
  const { usuario } = useAuth()
  const { enqueueSnackbar } = useSnackbar()

  const [todas, setTodas] = useState<SolicitacaoRenovacao[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [filtro, setFiltro] = useState<'abertas' | 'todas'>('abertas')

  // Diálogo de decisão
  const [alvo, setAlvo] = useState<SolicitacaoRenovacao | null>(null)
  const [decisao, setDecisao] = useState<Decisao>('APROVADA')
  const [prazoConcedido, setPrazoConcedido] = useState('')
  const [parecer, setParecer] = useState('')
  const [erroDecisao, setErroDecisao] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const podeAnalisar = useMemo(
    () => (usuario?.permissoes ?? []).some((p) => ROLES_ANALISE.includes(p)),
    [usuario?.permissoes],
  )

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      // O backend já escopa pelo papel: consultor vê as próprias, Originei vê as do tenant.
      setTodas(await renovacaoService.listar())
    } catch {
      setErro('Não foi possível carregar as solicitações de renovação.')
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const lista = useMemo(
    () => (filtro === 'abertas' ? todas.filter((s) => EM_ABERTO.includes(s.status)) : todas),
    [todas, filtro],
  )
  const qtdAbertas = useMemo(() => todas.filter((s) => EM_ABERTO.includes(s.status)).length, [todas])

  const abrirDecisao = (s: SolicitacaoRenovacao, tipo: Decisao) => {
    setAlvo(s)
    setDecisao(tipo)
    // Por padrão concede exatamente o que foi pedido; o operador pode reduzir.
    setPrazoConcedido(s.novoPrazoSolicitado)
    setParecer('')
    setErroDecisao(null)
  }

  const fecharDecisao = () => {
    setAlvo(null)
    setErroDecisao(null)
  }

  const confirmarDecisao = async () => {
    if (!alvo) return
    setSalvando(true)
    setErroDecisao(null)
    try {
      const atualizada = await renovacaoService.analisar(alvo.id, {
        novoStatus: decisao,
        prazoConcedido: decisao === 'APROVADA' ? prazoConcedido : undefined,
        parecer: parecer.trim() || undefined,
      })
      setTodas((prev) => prev.map((s) => (s.id === alvo.id ? atualizada : s)))
      enqueueSnackbar(
        decisao === 'APROVADA'
          ? `Renovação aprovada — novo prazo ${formatDate(prazoConcedido)}`
          : 'Renovação reprovada',
        { variant: decisao === 'APROVADA' ? 'success' : 'info' },
      )
      setAlvo(null)
    } catch (e) {
      setErroDecisao(e instanceof Error ? e.message : 'Não foi possível registrar a decisão')
    } finally {
      setSalvando(false)
    }
  }

  const cancelarPedido = async (s: SolicitacaoRenovacao) => {
    try {
      const atualizada = await renovacaoService.cancelar(s.id)
      setTodas((prev) => prev.map((x) => (x.id === s.id ? atualizada : x)))
      enqueueSnackbar('Solicitação cancelada', { variant: 'info' })
    } catch (e) {
      enqueueSnackbar(
        e instanceof Error ? e.message : 'Não foi possível cancelar a solicitação',
        { variant: 'error' },
      )
    }
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="h5">
            {podeAnalisar ? 'Solicitações de Renovação' : 'Minhas Solicitações de Renovação'}
          </Typography>
          {qtdAbertas > 0 && (
            <Chip color="warning" size="small" label={`${qtdAbertas} aguardando decisão`} />
          )}
        </Stack>
        <ToggleButtonGroup size="small" exclusive value={filtro} onChange={(_, v) => v && setFiltro(v)}>
          <ToggleButton value="abertas">Em aberto</ToggleButton>
          <ToggleButton value="todas">Todas</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <Card>
        <CardContent>
          {carregando ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : lista.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              {filtro === 'abertas'
                ? 'Nenhuma solicitação aguardando decisão.'
                : 'Nenhuma solicitação de renovação.'}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Solicitada em</TableCell>
                    <TableCell>Prazo atual</TableCell>
                    <TableCell>Prazo pedido</TableCell>
                    <TableCell>Justificativa</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lista.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell>{formatDate(s.solicitadoEm)}</TableCell>
                      <TableCell>{s.prazoAnterior ? formatDate(s.prazoAnterior) : '—'}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {formatDate(s.novoPrazoSolicitado)}
                        {s.prazoAprovado && s.prazoAprovado !== s.novoPrazoSolicitado && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            concedido: {formatDate(s.prazoAprovado)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="body2">{s.justificativa}</Typography>
                        {s.parecer && (
                          <Typography variant="caption" color="text.secondary">
                            Parecer: {s.parecer}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={STATUS_COR[s.status]} label={STATUS_LABEL[s.status]} />
                      </TableCell>
                      <TableCell align="right">
                        {EM_ABERTO.includes(s.status) &&
                          (podeAnalisar ? (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button size="small" onClick={() => abrirDecisao(s, 'REPROVADA')}>
                                Reprovar
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => abrirDecisao(s, 'APROVADA')}
                              >
                                Aprovar
                              </Button>
                            </Stack>
                          ) : (
                            <Button size="small" onClick={() => cancelarPedido(s)}>
                              Cancelar pedido
                            </Button>
                          ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ── MODAL: decisão da Originei ── */}
      <Dialog open={!!alvo} onClose={fecharDecisao} maxWidth="xs" fullWidth>
        {alvo && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>
              {decisao === 'APROVADA' ? 'Aprovar renovação' : 'Reprovar renovação'}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Pedido: {alvo.prazoAnterior ? formatDate(alvo.prazoAnterior) : '—'} →{' '}
                <strong>{formatDate(alvo.novoPrazoSolicitado)}</strong>
              </Typography>
              {erroDecisao && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {erroDecisao}
                </Alert>
              )}
              {decisao === 'APROVADA' && (
                <TextField
                  label="Prazo concedido"
                  type="date"
                  fullWidth
                  value={prazoConcedido}
                  onChange={(e) => setPrazoConcedido(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Pode ser menor que o pedido. Aprovar já estende o prazo da reserva."
                  sx={{ mb: 2 }}
                />
              )}
              <TextField
                label={decisao === 'REPROVADA' ? 'Parecer' : 'Parecer (opcional)'}
                required={decisao === 'REPROVADA'}
                fullWidth
                multiline
                minRows={2}
                value={parecer}
                onChange={(e) => setParecer(e.target.value)}
                helperText={
                  decisao === 'REPROVADA'
                    ? 'O consultor precisa saber o motivo da reprovação.'
                    : undefined
                }
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={fecharDecisao} disabled={salvando}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                color={decisao === 'APROVADA' ? 'primary' : 'error'}
                onClick={confirmarDecisao}
                disabled={
                  salvando ||
                  (decisao === 'APROVADA' && !prazoConcedido) ||
                  (decisao === 'REPROVADA' && !parecer.trim())
                }
                startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {decisao === 'APROVADA' ? 'Aprovar' : 'Reprovar'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  )
}

export default RenovacoesPage
